import { Component, type ErrorInfo, type ReactNode } from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';
import { R, ft, btnAccent, btnOutline, shadow } from '../lib/ui';

interface Props {
  children: ReactNode;
  fallback?: (error: Error, reset: () => void) => ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, info);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError && this.state.error) {
      if (this.props.fallback) {
        return this.props.fallback(this.state.error, this.handleReset);
      }

      return (
        <div
          style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 32,
            background: R.bg,
            color: R.text,
            height: '100%',
            minHeight: 300,
          }}
        >
          <div
            style={{
              maxWidth: 520,
              width: '100%',
              padding: '28px 24px',
              background: R.bgSubtle,
              border: ft.strong,
              boxShadow: shadow.panel,
              borderRadius: 4,
              display: 'flex',
              flexDirection: 'column',
              gap: 16,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  background: R.dangerSubtle,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: R.danger,
                  flexShrink: 0,
                }}
              >
                <AlertTriangle size={20} />
              </div>
              <div>
                <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: R.text }}>
                  Произошла ошибка при отображении
                </h3>
                <p style={{ margin: '4px 0 0', fontSize: 12.5, color: R.textMuted }}>
                  Интерфейс временно недоступен из-за сбоя рендеринга
                </p>
              </div>
            </div>

            <div
              style={{
                padding: 12,
                borderRadius: 4,
                background: R.bgInput,
                border: ft.hair,
                fontSize: 12,
                fontFamily: 'monospace',
                color: R.danger,
                wordBreak: 'break-word',
                maxHeight: 140,
                overflowY: 'auto',
              }}
            >
              {this.state.error.message || String(this.state.error)}
            </div>

            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 4 }}>
              <button
                type="button"
                style={btnAccent}
                onClick={this.handleReset}
              >
                <RefreshCw size={13} /> Попробовать снова
              </button>
              <button
                type="button"
                style={btnOutline}
                onClick={() => {
                  window.location.href = '/';
                }}
              >
                <Home size={13} /> На главную
              </button>
              <button
                type="button"
                style={{ ...btnOutline, marginLeft: 'auto' }}
                onClick={() => window.location.reload()}
              >
                Перезагрузить страницу
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
